import { Badge, ScoreRing, GateDot, StatCell } from "./Primitives.jsx";
import { sig, T } from "../lib/design.js";
import { findSectorForTicker } from "../lib/universe.js";
import { fmt, fmtPct, fmtDol, fmtK } from "../lib/scoring.js";

function OptionsBox({ opts }) {
  if (!opts?.contracts?.length) return null;
  const best = opts.contracts[opts.best_contract_idx ?? 0] ?? opts.contracts[0];
  const ivrCol = opts.iv_rank < 30 ? "#22c55e" : opts.iv_rank < 50 ? "#f59e0b" : "#f87171";
  return (
    <div
      style={{
        borderRadius: 8,
        border: "0.5px solid rgba(96,165,250,0.35)",
        background: "rgba(96,165,250,0.05)",
        padding: "9px 11px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#60a5fa", fontFamily: T.mono }}>
          ${best.strike}C · {opts.expiry_date} · {opts.dte}d
        </span>
        <Badge label={`IVR ${opts.iv_rank}`} col={ivrCol} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 5, marginBottom: 7 }}>
        <StatCell label="Premium" value={fmtDol(best.premium)} />
        <StatCell label="Break-even" value={fmtDol(best.breakeven)} col="#f59e0b" />
        <StatCell label="Max loss" value={`$${Math.round((best.premium || 0) * 100)}`} col="#f87171" />
        <StatCell label="OI" value={fmtK(best.open_interest)} />
        <StatCell label="Volume" value={fmtK(best.volume)} />
        <StatCell label="IV" value={fmt(best.iv_pct, 1) + "%"} />
      </div>
      <div style={{ fontSize: 10.5, color: T.textSecondary }}>
        <span style={{ color: "#22c55e" }}>Bid</span> {fmtDol(best.bid)} ·{" "}
        <span style={{ color: "#f87171" }}>Ask</span> {fmtDol(best.ask)}
      </div>
    </div>
  );
}

function AIBlock({ ai }) {
  if (!ai) return null;
  const col = sig(ai.signal).fg;
  const bc = ai.best_contract;
  return (
    <div
      style={{
        borderRadius: 8,
        borderLeft: `3px solid ${col}`,
        background: sig(ai.signal).bg,
        padding: "9px 11px",
        fontSize: 11.5,
        lineHeight: 1.5,
        color: T.textSecondary,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 12.5, color: col }}>
          ✦ AI: {ai.signal?.replace("_", " ")}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted }}>score {ai.score}</span>
      </div>
      {bc && (
        <div
          style={{
            fontFamily: T.mono,
            fontSize: 11,
            color: T.textPrimary,
            background: "rgba(0,0,0,0.04)",
            borderRadius: 6,
            padding: "6px 9px",
            marginBottom: 7,
          }}
        >
          <div style={{ fontWeight: 600, color: "#60a5fa", marginBottom: 3 }}>
            ${bc.strike}C · {bc.expiry} · {bc.dte}d · {fmtDol(bc.premium)} · BE {fmtDol(bc.breakeven)}
          </div>
          {bc.rationale && (
            <div style={{ fontSize: 10.5, color: T.textSecondary, lineHeight: 1.4 }}>{bc.rationale}</div>
          )}
        </div>
      )}
      <div style={{ marginBottom: ai.risk_notes ? 5 : 0 }}>{ai.entry_notes}</div>
      {ai.risk_notes && <div style={{ color: "#f87171", marginTop: 4 }}>⚠ {ai.risk_notes}</div>}
      {ai.sector_context && (
        <div style={{ color: T.textMuted, marginTop: 4, fontSize: 11 }}>📊 {ai.sector_context}</div>
      )}
      {ai.tv_alerts?.length > 0 && (
        <div style={{ marginTop: 9, paddingTop: 7, borderTop: "0.5px solid rgba(0,0,0,0.08)" }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: T.textMuted,
              marginBottom: 5,
            }}
          >
            TradingView alerts
          </div>
          {ai.tv_alerts.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 5, marginBottom: 3, fontSize: 11 }}>
              <span style={{ color: "#60a5fa", flexShrink: 0 }}>→</span>
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TickerCard({ r }) {
  const { fg, bg, border } = sig(r.signal);
  const sector = r.sector || findSectorForTicker(r.ticker) || "";
  const changeCol = (r.change_pct ?? 0) >= 0 ? "#22c55e" : "#f87171";
  const ai = r.aiResult;
  const opts = ai?.options;

  return (
    <div
      style={{
        background: T.bg2,
        borderRadius: 12,
        padding: "13px 14px",
        border: r.signal === "STRONG" ? `1.5px solid ${fg}` : `0.5px solid ${T.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 9,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: T.textPrimary, fontFamily: T.mono }}>
              {r.ticker}
            </span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 7px",
                borderRadius: 20,
                background: bg,
                color: fg,
                border: `0.5px solid ${border}`,
              }}
            >
              {r.signal.replace("_", " ")}
            </span>
            {r.signal === "STRONG" && <Badge label="🔔 entry" col="#22c55e" />}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, display: "flex", gap: 7, flexWrap: "wrap" }}>
            <span
              style={{
                maxWidth: 140,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {r.name}
            </span>
            {sector && (
              <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 3, background: "rgba(0,0,0,0.05)" }}>
                {sector}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, fontFamily: T.mono }}>
            {fmtDol(r.price)}
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: changeCol }}>
            {fmtPct(r.change_pct ?? 0)}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
        {r.rsi && (
          <Badge label={`RSI ${r.rsi}`} col={r.rsi > 50 && r.rsi < 70 ? "#22c55e" : T.textMuted} />
        )}
        {r.vol_ratio && (
          <Badge label={`Vol ${fmt(r.vol_ratio, 1)}×`} col={r.vol_ratio > 1.5 ? "#22c55e" : T.textMuted} />
        )}
        {r.momentum_pct > 0 && <Badge label={`+${fmt(r.momentum_pct, 1)}%`} col="#f59e0b" />}
        {r.setup_type && <Badge label={r.setup_type} col="#a855f7" />}
        <div style={{ marginLeft: "auto" }}>
          <ScoreRing score={r.score} signal={r.signal} />
        </div>
      </div>

      {r.catalyst && (
        <div
          style={{
            fontSize: 11,
            color: T.textSecondary,
            background: "rgba(168,85,247,0.05)",
            borderLeft: "2px solid rgba(168,85,247,0.4)",
            padding: "5px 9px",
            borderRadius: "0 5px 5px 0",
            lineHeight: 1.4,
          }}
        >
          {r.catalyst}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 10px" }}>
        <GateDot
          label="9 EMA retest"
          active={Math.abs(r.ema_diff_pct || 99) < 1.5 && !!r.trend_up}
          tip={`EMA diff ${fmt(r.ema_diff_pct || 0)}%`}
        />
        <GateDot
          label="Volume surge 1.5×"
          active={(r.vol_ratio || 0) > 1.5}
          tip={`${fmt(r.vol_ratio || 0, 1)}× 20d avg`}
        />
        <GateDot label="Momentum +6%" active={(r.momentum_pct || 0) >= 6} />
        <GateDot label="Above MAs" active={!!r.trend_up && !!r.above_50d_ma} />
      </div>

      {opts && <OptionsBox opts={opts} />}
      {ai && <AIBlock ai={ai} />}

      {r.analyzing && (
        <div style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic", textAlign: "center", padding: "4px 0" }}>
          ⟳ Analyzing options chain & strategy fit…
        </div>
      )}

      <a
        href={`https://www.tradingview.com/chart/?symbol=${r.ticker}`}
        target="_blank"
        rel="noreferrer"
        style={{
          fontSize: 11.5,
          padding: "6px 0",
          borderRadius: 7,
          border: "0.5px solid rgba(96,165,250,0.4)",
          background: "transparent",
          color: "#60a5fa",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Open in TradingView ↗
      </a>
    </div>
  );
}
